import { EnvelopeIcon, PhoneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { PipelineEntry } from '../types';

interface LeadDetailDrawerProps {
  lead: PipelineEntry | null;
  onClose: () => void;
  onViewCustomer: (customerId: string) => void;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);

const LeadDetailDrawer: React.FC<LeadDetailDrawerProps> = ({ lead, onClose, onViewCustomer }) => {
  const { t } = useTranslation(['crm', 'common']);

  if (!lead) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-detail-title"
    >
      <div className="fixed inset-0 bg-black/30" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <h2 id="lead-detail-title" className="text-xl font-bold text-gray-900">
            {t('pipeline.detail.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('pipeline.detail.close')}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {lead.customer.first_name} {lead.customer.last_name}
        </h3>
        <div className="mt-2 space-y-1 text-sm text-gray-600">
          <p className="flex items-center">
            <PhoneIcon className="mr-2 h-4 w-4" />
            {lead.customer.phone}
          </p>
          <p className="flex items-center">
            <EnvelopeIcon className="mr-2 h-4 w-4" />
            {lead.customer.email}
          </p>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-gray-500">{t('pipeline.detail.leadSource')}</dt>
            <dd className="font-medium text-gray-900">{lead.lead_source || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('pipeline.detail.estimatedValue')}</dt>
            <dd className="font-medium text-gray-900">{formatCurrency(lead.estimated_value)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('pipeline.detail.probability')}</dt>
            <dd className="font-medium text-gray-900">{lead.probability}%</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('pipeline.detail.activities')}</dt>
            <dd className="font-medium text-gray-900">{lead.activity_count}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('pipeline.detail.pendingTasks')}</dt>
            <dd className="font-medium text-gray-900">{lead.pending_tasks}</dd>
          </div>
        </dl>

        <button
          type="button"
          onClick={() => onViewCustomer(lead.customer.id)}
          className="btn-primary mt-6 w-full"
        >
          {t('pipeline.detail.viewCustomerActivity')}
        </button>
      </div>
    </div>
  );
};

export default LeadDetailDrawer;
